/* tslint:disable:no-trailing-whitespace import-spacing no-inferrable-types typedef-whitespace */

import {SimulationJob, SimulationJobType, SimulationStatusType} from './simulation.job';
import {RemoteSimJob} from "@/models/sim/remote.sim.job";
import {SimConfig} from "@/models/sim/sim.config";
import {isset} from "@/util/util";

/**
 *
 * @class SimpleSimJob
 *
 * @brief simple simulation jobs are the ones we directly run from a blob (a scenario), these are intended
 * for interactive usage; run it, get a quick result back, tweak it, run it again...
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      June 2020
 *
 */
export class SimpleSimJob extends SimulationJob {

  /** remote download path for the simulation input */
  public  inputDownload: string     = '';

  /** remote download path for the simulation output */
  public outputDownload: string     = '';

  /** blob key for output, once downloaded */
  public localOutputKey: string     = '';

  /** remote download path for the key performance indicators */
  public kpiDownload: string        = '';

  /** remote download path for the key performance indicators (extended) */
  public kpixDownload: string       = '';

  /** blob key for KPI, once downloaded */
  public  localKPIkey:   string     = '';

  /** blob key for KPIX, once downloaded */
  public  localKPIXkey:  string     = '';

  /** remote download path for line simulator command file*/
  public cmdDownload: string        = '';

  /** blob key for cmd, once downloaded */
  public  localCmdkey:   string     = '';

  /** remote download path for scenario validation / simulation errors */
  public errorsDownload: string     = '';

  /** blob key for errors, once downloaded */
  public  localErrorskey:   string  = '';

  /** remote download path for the simulation log file */
  public    logDownload: string     = '';

  /** blob key for log, once downloaded */
  public  localLogkey:   string     = '';

  /** extra details like timing metrics */
  public          extra: any        = null;

  /** the most recent error before the simulation ended */
  public      lastError: string     = '';

  /** the current (known) job progress */
  public       progress: number     = 0;

  /** the simulation status text while the simulation runs */
  public       response: string     = '';

  /** the options the job was run with */
  public        options: any        = null;

  public constructor(init?: Partial<SimpleSimJob>) {

    super(init);

    (<any>Object).assign(this, init);

    this.checkResolved();
  }

  /** check the status and checkpoints to see if this job is resolved or not.  */
  checkResolved(): void {

    this.resolved = false;

    /* if we got to the downloaded check point...resolved. */

    if (this.details) {

      let meta = this.details.extra;

      if (isset(meta.checkpoints) && isset(meta.checkpoints.download_complete)) {

        /* we downloaded the outputs, so it must be ready. */

        this.resolved = true;
      }
    }

    /* if we clearly failed or clearly completed...resolved. */

    if (this.status == SimulationStatusType.ABORTED  ||
        this.status == SimulationStatusType.CANCELED ||
        this.status == SimulationStatusType.FAIL     ||
        this.status == SimulationStatusType.FAILED   ||
        this.status == SimulationStatusType.SUCCESS  ||
        this.status == SimulationStatusType.DEATH) {

      this.resolved = true;
    }


  }

  /**
   * all implementations must return a simlified version of themselves, that is appropriate for use
   * with JSON.stringify().
   */
  toJSON(init?: any): any  {

    let details: null|RemoteSimJob = this.details;

    if (this.details) {

      details = details.toJSON()
    }

    return super.toJSON({

      inputdownload:  this.inputDownload,
      outputdownload: this.outputDownload,
      cmddownload:    this.cmdDownload,
      kpidownload:    this.kpiDownload,
      kpxdownload:    this.kpixDownload,
      errorsdownload: this.errorsDownload,
      logdownload:    this.logDownload,
      locallogkey:    this.localLogkey,
      localoutputkey: this.localOutputKey,
      localcmdkey:    this.localCmdkey,
      localkpikey:    this.localKPIkey,
      localkpixkey:   this.localKPIXkey,
      localerrorskey: this.localErrorskey,
      extra:          this.extra,
      lasterror:      this.lastError,
      progress:       this.progress,
      response:       this.response,
      options:        this.options,
      details

    });
  }

  /**
   * all implementations must be able to recover themselves from the given object which was
   * deserialized via JSON.parse().
   */
  fromJSON(json: any): void {

    super.fromJSON(json);

    this.inputDownload  = json.inputdownload;
    this.outputDownload = json.outputdownload;
    this.cmdDownload    = json.cmddownload;
    this.kpiDownload    = json.kpidownload;
    this.kpixDownload   = json.kpixdownload;
    this.errorsDownload = json.errorsdownload;
    this.logDownload    = json.logdownload;
    this.localLogkey    = json.locallogkey;
    this.localOutputKey = json.localoutputkey;
    this.localCmdkey    = json.localcmdkey;
    this.localKPIXkey   = json.localkpixkey;
    this.localErrorskey = json.localerrorskey;
    this.extra          = json.extra;
    this.lastError      = json.lasterror;
    this.progress       = json.progress;
    this.response       = json.response;
    this.options        = json.options;
    this.details        = null;

    if (typeof json.localkpikey !== 'undefined') {

      this.localKPIkey  = json.localkpikey;
    }

    if (json.details) {

      this.details = new RemoteSimJob(json.details);
    }

    if (typeof this.progress === 'undefined') {

      this.progress = 0;
    }

    if (typeof this.response === 'undefined') {

      this.response = '';
    }
  }

  /** stamp out a SimpleSimJob from a RemoteSimJob */
  static fromRemoteJob(job: RemoteSimJob, engine: string): SimpleSimJob {

    /* recover the simulation configuration from the options and meta data of the remote job */

    let config: SimConfig = new SimConfig({
      dispatch:    SimConfig.dispatchType(job.options.dispatch),
      maxDays:     job.options.max_days,
      maxTime:     job.options.max_time,
      maxMemory:   job.options.max_memory,
      lockupHours: job.options.lockup_hours,
      logLevel:    SimConfig.logLevelType(job.options.loglevel),
      permissive:  job.options.permissive,
      priority:    job.options.priority,
      userlog:     job.options.userlog,
      traceStart:  job.options.trace_start,
      traceEnd:    job.options.trace_end,
      endpoint:    SimConfig.endpointOfEngine(engine),
      engine,
    });

    /*
     * in some cases we have the new style RemoteSimJob data in hand, which has a lot
     * more detail for a job that SimpleSimJob has.  But...some parts of the GUI still
     * only know about SimpleSimJob.  So, to eas the transition, we allow a RemoteSimJob
     * to be cast to a SimpleSimJob..that has the exact same 'details' but also its own
     * subset of fields filled in.
     *
     */

    let newJob: SimpleSimJob = new SimpleSimJob({
      type:           SimulationJobType.SIMPLE_SIM,
      jobId:          job.id,
      jobUuid:        `${engine}-sim-${job.id}`,
      status:         job.status,
      inputDownload:  job.importfile,
      outputDownload: job.output,
      cmdDownload:    job.simcmd,
      kpiDownload:    job.kpi,
      kpixDownload:   job.kpix,
      errorsDownload: job.simerrors,
      logDownload:    job.log,
      localLogkey:    '',
      localOutputKey: '',
      localCmdkey:    '',
      localKPIXkey:   '',
      localErrorskey: '',
      extra:          job.extra,
      lastError:      job.lasterror,
      progress:       parseFloat(job.progress),
      response:       job.response,
      options:        job.options,
      details:        job,
      expiry:         job.expiry.toJSDate(),
      dequeue:        job.dequeue_at.toJSDate(),
      updated:        job.updated_at.toJSDate(),
      created:        job.created_at.toJSDate(),
      config
    });

    newJob.checkResolved();

    return newJob;

  }
}
